import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { ForgotPasswordNewPasswordDto } from "../../../model/user/dto/ForgotPasswordNewPasswordDto";
import { useAppDispatch } from "../../../redux/store";
import { userActions } from "../../../redux/user/user.slice";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { useNavigate } from "react-router-dom";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { TFunction } from "i18next";

const validatePasswordsMatch = (
  t: TFunction,
  newPasswordConfirmation?: string,
  newPassword?: string,
) => {
  if (newPasswordConfirmation === newPassword) {
    return true;
  }

  return t("resetPassword.newPass.mismatch");
};

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const StyledInput = styled(Input)`
  width: 23rem;
  height: 2.3rem;
  border: 1px solid ${(props) => props.theme.lightergray};
  padding: ${Spacing.s};
  transition: border 0.2s;

  &:focus {
    border: 1px solid #86b7fe !important;
  }
`;

export const ForgotPasswordNewPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, getValues, setError } = useFormContext<
    ForgotPasswordNewPasswordDto & { newPasswordConfirm: string }
  >();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onNext = async () => {
    try {
      setLoading(true);

      await dispatch(
        userActions.forgotPasswordNewPassword({
          newPassword: getValues("newPassword"),
          token: getValues("token"),
        }),
      ).unwrap();
      AtiraToast.success(t("resetpassword.newPass.success"));
      navigate("/login");
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex justifyContent="center" flexDirection="column" width={"100%"} gap="s">
      <Text color="gray" align="center">
        {t("resetpassword.newPass.title")}
      </Text>
      <Controller
        name="newPassword"
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <StyledInput
            title={t("resetpassword.newPass.input.title")}
            value={value}
            onChange={onChange}
            type="password"
            id="newpassword"
            valid={!error}
            errorMessage={error?.message}
            required
          />
        )}
      />

      <Controller
        name="newPasswordConfirm"
        control={control}
        rules={{
          required: true,
          validate: (value) =>
            validatePasswordsMatch(t, value, getValues("newPassword")),
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Flex marginTop="s">
            <StyledInput
              title={t("resetpassword.newPass.confirm.input.title")}
              value={value}
              onChange={onChange}
              type="password"
              id="newpasswordConfirm"
              valid={!error}
              errorMessage={error?.message}
              required
            />
          </Flex>
        )}
      />
      <StyledButton onClick={handleSubmit(onNext)} loading={loading}>
        {t("resetpassword.newPass.button")}
      </StyledButton>
    </Flex>
  );
};
